import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { UpdateDataTokenCode } from 'src/app/shared/class/update-data';
import { CreditTypeId } from 'src/app/shared/enums/credit-type.enum';
import { Cause, StepAboutYouMX } from 'src/app/shared/interfaces/step-data';
import { RequestInformationComponent } from 'src/app/shared/request-information/request-information.component';
import { PutDataService } from 'src/app/shared/services/put-data.service';
import { RequestFormService } from 'src/app/shared/services/request-form.service';
import { AutoUnsubscribeComponent } from 'src/app/shared/utils/auto-unsubscribe.component';
import { FormsValidators } from 'src/app/shared/utils/forms-validators';

@Component({
  selector: 'app-step-sobre-ti',
  templateUrl: './step-sobre-ti.component.html',
  styleUrls: ['./step-sobre-ti.component.scss'],
})
export class StepSobreTiComponent
  extends AutoUnsubscribeComponent
  implements OnInit
{
  @Input() saveRequest!: Observable<boolean>;
  @Output() formValidEvent = new EventEmitter<boolean>();
  @Output() stepAboutYouSavedEvent = new EventEmitter<boolean>();
  public formStepAboutYou: FormGroup;
  protected _onDestroy = new Subject<void>();
  public referalQr!: string;
  public codeReferral!: string;
  public creditTypeUpdate!: string;

  constructor(
    private frmBuilder: FormBuilder,
    private putDataService: PutDataService,
    private requestFormService: RequestFormService,
    private router: Router,
    private dialog: MatDialog,
    private activatedRouter: ActivatedRoute
  ) {
    super();

    this.activatedRouter.queryParams.subscribe((params) => {
      this.referalQr = params['referall'];
      this.codeReferral = params['referralCode'];
    });

    this.formStepAboutYou = this.frmBuilder.group({
      hasGuerreroLicense: ['', [Validators.required]],
      canFullTime: ['', [Validators.required]],
      hasUberBlocked: ['', [Validators.required]],
      liveCdmxOrMetropolitana: ['', [Validators.required]],
      salaryExpectation: ['', [Validators.required, Validators.maxLength(7)]],
      hasNightAvailability: ['', [Validators.required]],
    });
  }

  ngOnInit(): void {
    if (
      this.requestFormService.getUpdateForm() &&
      this.getStepNumber() > 1 &&
      this.getCreditType() === CreditTypeId.DAE
    ) {
      this.loadUpdateFormStepAboutYou();
    }

    this.saveRequest.subscribe((value) => {
      if (value) {
        if (this.formStepAboutYou.dirty) {
          this.saveStepAboutYou();
        } else {
          this.stepAboutYouSavedEvent.emit(true);
        }
      }
    });
  }

  checkEvent() {
    this.formValidEvent.emit(this.formStepAboutYou.valid);
  }

  getStepAboutYouObject(): StepAboutYouMX {
    return {
      ...this.formStepAboutYou.value,
    };
  }

  saveStepAboutYou() {
    if (!this.formStepAboutYou.valid) {
      FormsValidators.markFormGroupTouched(this.formStepAboutYou);
    }
    const putUpdateRequestAboutYou$ = this.putDataService
      .putUpdateRequestAboutYou(
        this.getCountry().toUpperCase(),
        this.getApplicationId(),
        this.getStepAboutYouObject()
      )
      .subscribe({
        next: (resp) => {
          sessionStorage.setItem(
            'approved',
            resp.approved === true ? 'true' : 'false'
          );
          resp.approved === false
            ? this.openStopAdvanceDialog(resp.causes![0])
            : this.stepAboutYouSavedEvent.emit(true);
        },
        error: (err) => {
          this.openRequestInformationDialog(err);
        },
      });
    this.subscriptions.push(putUpdateRequestAboutYou$);
  }

  loadUpdateFormStepAboutYou() {
    this.buildForm(this.requestFormService.getUpdateForm());
    this.checkEvent();
  }

  buildForm(value: UpdateDataTokenCode) {
    this.formStepAboutYou.patchValue({
      hasGuerreroLicense: value.prefilter.hasGuerreroLicense,
      canFullTime: value.prefilter.canFullTime,
      hasUberBlocked: value.prefilter.hasUberBlocked,
      liveCdmxOrMetropolitana: value.prefilter.liveCdmxOrMetropolitana,
      salaryExpectation: value.prefilter.salaryExpectation,
      hasNightAvailability: value.prefilter.hasNightAvailability,
    });
  }

  openStopAdvanceDialog(event?: Cause) {
    let errorMessage =
      'Lo siento, no podemos procesar tu solicitud ya que no cumple con los requisitos mínimos.';

    const causeLicence = `LICENCE`.match(event!.code);
    const causeAvailability = `AVAILABILITY`.match(event!.code);
    const causeUber = `UBER`.match(event!.code);
    const causeSalary = `SALARY`.match(event!.code);
    const causeLocation = `LOCATION`.match(event!.code);
    const causeAge = `AGE`.match(event!.code);

    if (causeAge) {
      errorMessage =
        'Lo siento, no podemos procesar tu solicitud ya que la edad se encuentra fuera del rango aceptado.';
    }
    if (causeLicence) {
      errorMessage =
        'Lo siento, no podemos procesar tu solicitud ya que el tipo de licencia ingresado no es aceptado.';
    }
    if (causeLocation) {
      errorMessage =
        'Lo siento, no podemos procesar tu solicitud ya que la localidad no es aceptada.';
    }
    if (causeSalary) {
      errorMessage =
        'Lo siento, no podemos procesar tu solicitud ya que la expectativa salarial es alta.';
    }
    if (causeUber) {
      errorMessage =
        'Lo siento, no podemos procesar tu solicitud ya que no puede estar bloqueado en Uber.';
    }
    if (causeAvailability) {
      errorMessage =
        'Lo siento, no podemos procesar tu solicitud ya que la disponibilidad ingresada no es aceptada.';
    }

    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = 'min(400px,90%)';
    dialogConfig.maxWidth = 'none';
    dialogConfig.data = {
      title: 'Información de solicitud',
      question: errorMessage,
    };
    this.dialog
      .open(RequestInformationComponent, dialogConfig)
      .afterClosed()
      .subscribe(() => {
        this.router.navigate(['/busqueda']);
        sessionStorage.clear();
      });
  }

  openRequestInformationDialog(event: string | number, preFilter?: boolean) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      title: 'Información de solicitud',
      question: `Ha ocurrido un error`,
    };
    this.dialog
      .open(RequestInformationComponent, dialogConfig)
      .afterClosed()
      .subscribe(() => {
        if (preFilter === false) {
          this.openStopAdvanceDialog();
        } else {
          if (!(event === 400)) {
            this.stepAboutYouSavedEvent.emit(true);
          }
        }
      });
  }

  getCountry() {
    return !this.requestFormService.getUpdateForm()
      ? sessionStorage.getItem('language')!
      : this.requestFormService.getUpdateForm().countryCode.code.toLowerCase();
  }

  getCreditType() {
    if (this.requestFormService.getUpdateForm()) {
      this.creditTypeUpdate =
        this.requestFormService.getUpdateForm().creditType.code === 'DAE'
          ? '9'
          : '1';
    }
    return !this.requestFormService.getUpdateForm()
      ? sessionStorage.getItem('creditType')!
      : this.creditTypeUpdate;
  }

  getApplicationId() {
    return !this.requestFormService.getUpdateForm()
      ? sessionStorage.getItem('applicationId')!
      : this.requestFormService.getUpdateForm().id;
  }

  getStepNumber() {
    return !this.requestFormService.getUpdateForm()
      ? parseInt(sessionStorage.getItem('stepNumber')!)
      : this.requestFormService.getUpdateForm().lastSectionCompleted;
  }
}
